import React, { useState, useEffect, useRef } from 'react';
import { useStore } from '../../store';
import { getScannerContext } from '../../hooks/useScannerBridge';
import { useMarketData } from '../../hooks/useMarketData';
import { formatAge } from '../../selectors/viewHelpers';
import { getWatchlistRowSummary } from '../../selectors/watchlistSummaries';
import { getAlerts, subscribeAlerts, Alert, AlertSeverity } from '../alerts/alertStore';
import { useLiveQuote, setWatchedSymbols } from '../../hooks/liveQuoteStream';

const QUICK_SYMBOLS = ['SPY', 'QQQ', 'NVDA', 'AMD', 'MU', 'TSLA', 'META', 'MSFT', 'PLTR', 'AMZN'];

// ─── Alert color logic ────────────────────────────────────────────────────────
// Returns the highest-severity unread alert color for a symbol, or null

interface SymbolAlertState {
  color:   string;   // border/glow color
  bgTint:  string;   // very faint background tint
  label:   string;   // short badge label
  severity: AlertSeverity;
}

function getSymbolAlertState(symbol: string): SymbolAlertState | null {
  const alerts = getAlerts({ symbol, unreadOnly: true, limit: 20 });
  if (!alerts.length) return null;

  // Priority: invalidation > actionable > warning > info
  const sevPriority: AlertSeverity[] = ['invalidation', 'actionable', 'warning', 'info'];
  let top: Alert | null = null;

  for (const sev of sevPriority) {
    const match = alerts.find(a => a.severity === sev);
    if (match) { top = match; break; }
  }
  if (!top) return null;

  const cfg: Record<AlertSeverity, { color: string; bg: string; label: string }> = {
    invalidation: { color: '#EF4444', bg: '#EF444412', label: '✕ INV'  },
    actionable:   { color: '#22C55E', bg: '#22C55E12', label: '⚡ ACT'  },
    warning:      { color: '#F59E0B', bg: '#F59E0B12', label: '⚠ WARN' },
    info:         { color: '#60A5FA', bg: '#60A5FA10', label: '○ INFO' },
  };

  const c = cfg[top.severity];
  return { color: c.color, bgTint: c.bg, label: c.label, severity: top.severity };
}

// ─── Age label ────────────────────────────────────────────────────────────────

// ageLabel → formatAge from viewHelpers

// ─── Single watchlist row ─────────────────────────────────────────────────────

interface WLRowProps {
  symbol: string;
  isActive: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onRefresh: () => void;
}

function WLRow({ symbol, isActive, onSelect, onRemove, onRefresh }: WLRowProps) {
  const { symbolData, settings, weights } = useStore();
  const [alertState, setAlertState] = useState<SymbolAlertState | null>(() => getSymbolAlertState(symbol));
  const data    = symbolData[symbol];
  const candles = data?.candles;

  // Memoized selector — must come before price metrics (rvol comes from here)
  const rowSummary = getWatchlistRowSummary(symbol, data, settings, weights);

  // Subscribe to alert changes for live coloring
  useEffect(() => {
    setAlertState(getSymbolAlertState(symbol));
    const unsub = subscribeAlerts(() => setAlertState(getSymbolAlertState(symbol)));
    return unsub;
  }, [symbol]);

  // Live quote (Stage 17) — overrides bar close when fresh enough
  const liveQuote = useLiveQuote(symbol);

  // Price metrics
  // Stage 17: prefer live mid (5s polling) over the last bar close. Falls back
  // to bar close when live quote is missing or stale (>30s old). Bars are still
  // the source for prevPrice (open of first bar in window) and rvol.
  const barClose  = candles?.length ? candles[candles.length - 1].close : null;
  const liveAge   = liveQuote?.receivedAt ? (Date.now() - liveQuote.receivedAt) : Infinity;
  const liveFresh = liveQuote?.mid != null && liveAge < 30_000;
  const curPrice  = liveFresh ? liveQuote!.mid : barClose;
  const prevPrice = (candles?.length ?? 0) > 1 ? candles![0].open : null;
  const chg       = curPrice && prevPrice ? ((curPrice - prevPrice) / prevPrice * 100) : null;
  const rvol      = rowSummary.rvol;
  const age       = formatAge(liveFresh ? liveQuote!.receivedAt : (data?.lastUpdated || null));
  const isLive    = liveFresh || (!!data?.lastUpdated && (Date.now() - data.lastUpdated < 15000));

  // Regime + SMC — via memoized selector (declared above for correct ordering)
  const regimeLabel = rowSummary.regimeLabel;
  const regimeColor = rowSummary.regimeColor;
  const smcBias     = rowSummary.smcBias !== 'neutral'
    ? (rowSummary.smcBias === 'bullish' ? '▲' : '▼')
    : null;
  const smcColor    = rowSummary.smcBias === 'bullish' ? 'var(--green)'
    : rowSummary.smcBias === 'bearish' ? 'var(--red)' : 'var(--blue-dim)';

  const srcDot: Record<string, string> = {
    api: 'var(--green)', csv: 'var(--blue)', sample: 'var(--blue-dim)',
  };

  // ── Alert-driven border and background ──────────────────────────────────────
  // Precedence: invalidation (red pulse) > actionable (green glow) > warning (amber) > normal
  const hasAlert = !!alertState;
  const alertColor = alertState?.color;
  const alertBg    = alertState?.bgTint;

  // Active state uses cyan — clearly distinct from all four alert colors:
  //   invalidation=red, actionable=green, warning=amber, info=blue-light
  //   Active=cyan stands apart from all of them
  const ACTIVE_COLOR = 'var(--cyan)';
  const borderColor = isActive
    ? ACTIVE_COLOR
    : alertColor ?? 'var(--border)';
  const bgColor = isActive
    ? 'var(--surface2)'
    : alertBg ?? 'transparent';
  const borderWidth = (isActive || hasAlert) ? 2 : 1;
  const borderLeftColor = isActive ? ACTIVE_COLOR : alertColor ?? 'var(--surface)';

  // Pulse animation for invalidation alerts
  const pulseStyle = alertState?.severity === 'invalidation'
    ? { animation: 'blink 1.5s infinite' }
    : {};

  return (
    <div
      className={`wl-item ${isActive ? 'active' : ''}`}
      onClick={onSelect}
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
      role="button"
      aria-pressed={isActive}
      aria-label={`${symbol}${curPrice ? ` — $${curPrice.toFixed(2)}` : ''}${hasAlert ? ` — ${alertState!.label} alert` : ''}`}
      style={{
        ...pulseStyle,
        border: `${borderWidth}px solid ${borderColor}`,
        borderLeft: `3px solid ${borderLeftColor}`,
        borderRadius: 6,
        background: bgColor,
        marginBottom: 5,
        padding: '8px 10px',
        cursor: 'pointer',
        transition: 'border-color .2s, background .2s',
      }}
    >
      {/* Top row: ticker + alert badge + actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            className="wl-ticker"
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 15,
              fontWeight: 700,
              color: isActive ? 'var(--cyan)' : hasAlert ? alertColor : 'var(--text)',
              transition: 'color .15s',
              letterSpacing: '.5px',
            }}
          >
            {symbol}
          </span>

          {/* Alert badge — shows highest-severity unread alert */}
          {alertState && (
            <span
              title={`Unread ${alertState.severity} alert — click to view`}
              style={{
                fontSize: 7, fontFamily: 'var(--mono)', fontWeight: 700,
                color: alertColor, border: `1px solid ${alertColor}66`,
                background: `${alertColor}18`,
                padding: '0 4px', borderRadius: 3, flexShrink: 0,
                letterSpacing: '.5px',
              }}
            >
              {alertState.label}
            </span>
          )}

          {/* Scanner badge */}
          {(() => {
            const sc = getScannerContext(symbol);
            if (!sc) return null;
            const scoreColor = sc.score >= 4.5 ? 'var(--green)' : sc.score >= 4.0 ? '#4ADE80' : 'var(--amber)';
            return (
              <span title={`MER Scanner: ${sc.rating} | ${sc.key_catalyst}`} style={{
                fontSize: 8, fontFamily: 'var(--mono)', fontWeight: 700,
                color: scoreColor, border: `1px solid ${scoreColor}44`,
                background: `${scoreColor}12`,
                padding: '0 4px', borderRadius: 3, flexShrink: 0, cursor: 'help',
              }}>
                {sc.score.toFixed(1)}★
              </span>
            );
          })()}

          {/* Data source dot */}
          {data?.source && (
            <div
              title={`Data source: ${data.source}`}
              style={{
                width: 5, height: 5, borderRadius: '50%',
                background: srcDot[data.source] || 'var(--blue-dim)',
                flexShrink: 0,
              }}
            />
          )}

          {/* Live indicator */}
          {isLive && (
            <span style={{ fontSize: 8, color: 'var(--green)', fontFamily: 'var(--mono)', animation: 'blink 2s infinite' }}>
              ●
            </span>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 3 }}>
          <button
            onClick={e => { e.stopPropagation(); onRefresh(); }}
            title="Refresh data"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--blue-dim)', fontSize: 13, padding: '0 3px',
              fontFamily: 'var(--mono)', lineHeight: 1, transition: 'color .15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--gold)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--blue-dim)')}
          >⟳</button>
          <button
            onClick={e => { e.stopPropagation(); onRemove(); }}
            title="Remove from watchlist"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--blue-dim)', fontSize: 12, padding: '0 3px',
              fontFamily: 'var(--mono)', lineHeight: 1, transition: 'color .15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--red)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--blue-dim)')}
          >✕</button>
        </div>
      </div>

      {/* Loading state */}
      {data?.loading && (
        <div style={{ fontSize: 11, color: 'var(--gold)', fontFamily: 'var(--mono)' }}>
          Fetching…
        </div>
      )}

      {/* Error state */}
      {data?.error && !data?.loading && (
        <div
          style={{ fontSize: 10, color: 'var(--amber)', lineHeight: 1.4, marginTop: 2 }}
          title={data.error}
        >
          ⚠ {data.error.substring(0, 45)}{data.error.length > 45 ? '…' : ''}
        </div>
      )}

      {/* Price + metrics */}
      {curPrice && !data?.loading ? (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 5 }}>
            <span style={{
              fontFamily: 'var(--mono)', fontSize: 17,
              color: 'var(--text)', fontWeight: 600,
            }}>
              {curPrice.toFixed(2)}
            </span>
            {chg !== null && (
              <span style={{
                fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 700,
                color: chg >= 0 ? 'var(--green)' : 'var(--red)',
              }}>
                {chg >= 0 ? '+' : ''}{chg.toFixed(2)}%
              </span>
            )}
          </div>

          {/* Signal row */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{
              fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700,
              color: regimeColor, border: `1px solid ${regimeColor}44`,
              background: `${regimeColor}12`,
              padding: '1px 5px', borderRadius: 3, letterSpacing: '.5px',
            }}>{regimeLabel}</span>

            {rvol !== null && (
              <span style={{
                fontFamily: 'var(--mono)', fontSize: 10,
                color: rvol > 1.5 ? 'var(--green)' : rvol < 0.8 ? 'var(--red)' : 'var(--blue-muted)',
              }}>
                {rvol.toFixed(1)}x
              </span>
            )}

            {smcBias && (
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700, color: smcColor }}>
                {smcBias}
              </span>
            )}

            <div style={{ display: 'flex', gap: 3, marginLeft: 'auto', alignItems: 'center' }}>
              {data?.candles?.length && (
                <span style={{ fontSize: 7, fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--blue-dim)', letterSpacing: '.5px' }}>OHLCV</span>
              )}
              {data?.options?.length && (
                <span style={{ fontSize: 7, fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--purple)', letterSpacing: '.5px' }}>OI</span>
              )}
              {age && (
                <span style={{ fontSize: 9, fontFamily: 'var(--mono)', color: isLive ? 'var(--green)' : 'var(--blue-dim)', marginLeft: 2 }}>{age}</span>
              )}
            </div>
          </div>
        </>
      ) : !data?.loading && !data?.error ? (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
          <span style={{ fontSize: 11, color: 'var(--blue-dim)', fontFamily: 'var(--mono)' }}>No data</span>
          <button
            className="btn btn-sm"
            style={{ fontSize: 10, padding: '1px 7px', fontFamily: 'var(--mono)' }}
            onClick={e => { e.stopPropagation(); useStore.getState().loadSample(symbol); }}
          >Sample</button>
        </div>
      ) : null}
    </div>
  );
}

// ─── Main sidebar ─────────────────────────────────────────────────────────────

export function WatchlistSidebar() {
  const { watchlist, activeSymbol, addSymbol, removeSymbol, setActive, loadSample } = useStore();
  const { fetchSymbol } = useMarketData();
  const [input, setInput]         = useState('');
  const [showQuick, setShowQuick] = useState(false);
  const activeRef = useRef<HTMLDivElement>(null);

  const handleAdd = () => {
    const sym = input.trim().toUpperCase();
    if (!sym || sym.length > 10) return;
    addSymbol(sym);
    setInput('');
  };

  const handleSelect = (symbol: string) => { setActive(symbol); };

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeSymbol]);

  // Stage 17: keep the live-quote stream in sync with the watchlist composition.
  // Whenever symbols are added/removed, the stream's polling list is updated.
  useEffect(() => {
    setWatchedSymbols(watchlist.map((w: { symbol: string }) => w.symbol));
  }, [watchlist]);

  return (
    <div style={{ padding: '12px 10px', display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Header */}
      <div style={{
        fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700,
        letterSpacing: '2px', textTransform: 'uppercase',
        color: 'var(--gold)', marginBottom: 10,
        borderBottom: '1px solid var(--gold-dim)', paddingBottom: 8,
      }}>
        Watchlist
        {watchlist.length > 0 && (
          <span style={{ color: 'var(--blue-dim)', marginLeft: 8, fontWeight: 400 }}>
            {watchlist.length}
          </span>
        )}
      </div>

      {/* Alert color legend */}
      <div style={{
        display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8,
        padding: '4px 6px', background: 'var(--surface2)',
        borderRadius: 4, border: '1px solid var(--border)',
      }}>
        {[
          { color: '#EF4444', label: 'INV' },
          { color: '#22C55E', label: 'ACT' },
          { color: '#F59E0B', label: 'WARN' },
          { color: '#60A5FA', label: 'INFO' },
        ].map(({ color, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <div style={{ width: 3, height: 12, background: color, borderRadius: 2 }} />
            <span style={{ fontSize: 7, color, fontFamily: 'var(--mono)', fontWeight: 700, letterSpacing: '.5px' }}>
              {label}
            </span>
          </div>
        ))}
        <span style={{ fontSize: 7, color: 'var(--blue-dim)', fontFamily: 'var(--mono)', marginLeft: 'auto' }}>
          alerts
        </span>
      </div>

      {/* Add symbol input */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value.toUpperCase().replace(/[^A-Z0-9.-]/g, ''))}
          onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
          placeholder="Add ticker…"
          maxLength={10}
          style={{
            flex: 1, background: 'var(--navy)', border: '1px solid var(--border)',
            borderRadius: 6, color: 'var(--text)', padding: '6px 10px',
            fontFamily: 'var(--mono)', fontSize: 14, outline: 'none',
            transition: 'border-color .15s',
          }}
          onFocus={e => (e.target.style.borderColor = 'var(--gold)')}
          onBlur={e => (e.target.style.borderColor = 'var(--border)')}
        />
        <button
          className="btn btn-primary btn-sm"
          onClick={handleAdd}
          style={{ flexShrink: 0, fontFamily: 'var(--mono)', fontSize: 15, fontWeight: 700, padding: '4px 12px' }}
        >+</button>
      </div>

      {/* Symbol list */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {watchlist.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '24px 8px',
            color: 'var(--blue-dim)', fontFamily: 'var(--mono)',
            fontSize: 11, lineHeight: 1.8, letterSpacing: '.5px',
          }}>
            NO SYMBOLS<br/>
            <span style={{ fontSize: 10, color: 'var(--border)' }}>type ticker above</span>
          </div>
        ) : (
          watchlist.map(({ symbol }) => {
            const isActive = symbol === activeSymbol;
            return (
              <div key={symbol} ref={isActive ? activeRef : null}>
                <WLRow
                  symbol={symbol}
                  isActive={isActive}
                  onSelect={() => handleSelect(symbol)}
                  onRemove={() => removeSymbol(symbol)}
                  onRefresh={() => fetchSymbol(symbol)}
                />
              </div>
            );
          })
        )}
      </div>

      {/* Quick add presets */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 6 }}>
        <button
          style={{
            fontSize: 10, color: 'var(--blue-dim)', background: 'none', border: 'none',
            cursor: 'pointer', fontFamily: 'var(--mono)', letterSpacing: '.5px',
            marginBottom: showQuick ? 8 : 0, width: '100%', textAlign: 'left',
            transition: 'color .15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--gold)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--blue-dim)')}
          onClick={() => setShowQuick(!showQuick)}
        >
          {showQuick ? '▾' : '▸'} QUICK ADD
        </button>
        {showQuick && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {QUICK_SYMBOLS.map(s => (
              <button
                key={s}
                style={{
                  padding: '3px 7px', border: '1px solid var(--border)', borderRadius: 4,
                  background: 'var(--surface2)', color: 'var(--blue-muted)',
                  fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700,
                  cursor: 'pointer', transition: 'all .15s', letterSpacing: '.3px',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold-dim)'; e.currentTarget.style.color = 'var(--gold-light)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--blue-muted)'; }}
                onClick={() => loadSample(s)}
                title={`Load ${s} with sample data`}
              >{s}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
